import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output, ViewChild } from '@angular/core';
import Cropper from "cropperjs";

import { TranslateService } from '@ngx-translate/core';
import { DataURIToBlob } from './../../utils.js'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { environment } from './../../../environments/environment';
import { CommonModule } from '@angular/common';
import { IonicModule, AlertController, LoadingController, Platform, IonModal } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import heic2any from 'heic2any';

type ViewMode = 0 | 1 | 2 | 3;

@Component({
  selector: 'ngx-photo-editor',
  templateUrl: './ngx-photo-editor.component.html',
  styleUrls: ['./ngx-photo-editor.component.scss'],
  imports: [CommonModule, IonicModule, TranslateModule],
  standalone: true,
})
export class NgxPhotoEditorComponent implements OnDestroy {
  @ViewChild('modal', { static: false }) modal!: IonModal;
  @ViewChild('image') image!: ElementRef;

  isExist = false;
  isDisplay = false;
  private cropper!: Cropper;
  public imgUrl!: string | null;
  private objectUrl: string | null = null;
  private FILES_REGEX = /\.(gif|jpe?g|tiff|png|webp|bmp|pdf|jfif|heic|heif)$/i;
  loading: HTMLIonLoadingElement | null = null;

  @Input() automaticalyClose = false;
  @Input() modalTitle = 'Photo Editor';
  @Input() hideModalHeader = false;
  @Input() aspectRatio = NaN;
  @Input() autoCropArea = 1;
  @Input() autoCrop = true;
  @Input() mask = true;
  @Input() guides = true;
  @Input() centerIndicator = true;
  @Input() viewMode: ViewMode = 0;
  @Input() modalMaxWidth = '500px';
  @Input() modalCentered = false;
  @Input() scalable = true;
  @Input() zoomable = true;
  @Input() cropBoxMovable = true;
  @Input() cropBoxResizable = true;
  @Input() darkTheme = true;
  @Input() roundCropper = false;
  @Input() canvasHeight = 400;

  @Input() resizeToWidth: number | any;
  @Input() resizeToHeight: number | any;
  @Input() imageSmoothingEnabled = true;
  @Input() imageSmoothingQuality: ImageSmoothingQuality = 'high';
  @Input() format: string | any;

  @Input() closeBtnText = 'Close';
  @Input() applyBtnText = 'Apply';
  @Input() isEmbed: boolean = false;
  @Input() cropEnable: boolean = true;
  @Input() exportWhenUpdate: boolean = false;
  @Input() displayToolbar: boolean = true;

  quality = 92;

  @Input() set imageQuality(value: number) {
    if (value > 0 && value <= 100) {
      this.quality = value;
    } else {
      this.errorEvent.error('Invalid image quality');
    }
  }

  @Output() closeEvent = new EventEmitter<any>();
  @Output() imageCroppedEvent = new EventEmitter<NgxCroppedEvent>();
  @Output() errorEvent = new EventEmitter<any>();
  imageLoaded = false;
  isProcessing = false;




  @Input() set imageChangedEvent(data: File | string | any) {
    this.source = data;
    this.changeDetectorRef.detectChanges();
  }

  @Input() set source(data: File | string | any) {
    if (this.image != null) {
      if (this.cropper) {
        this.cropper.destroy();
        this.cropper = null as any;
      }
    }

    if (data == null) {
      this.isExist = false
      return
    }
    this.isExist = true;
    this.changeDetectorRef.detectChanges();

    if (data instanceof Event || (data && data.target && data.target.files)) {
      // @ts-ignore
      const file = data.target.files[0];
      console.log("NgxPhotoEditor: Event source detected", file);
      if (file && this.FILES_REGEX.test(file.name)) {
        if (!this.format) {
          this.format = file.type ? file.type.split('/')[1] : (file.name ? file.name.split('.').pop() : 'png');
        }
        console.log("NgxPhotoEditor: format set to", this.format);
        if (file.name && file.name.toLowerCase().endsWith('.pdf')) {
          const reader = new FileReader();
          this.imgUrl = null;
          reader.onload = (ev: any) => {
            this.pdfStringToBase64(ev.target.result)
            this.forceDetectChanges();
          };
          // @ts-ignore
          reader.readAsDataURL(data.target.files[0]);
        } else if (this.format == 'heic') {
          heic2any({
            blob: data.target.files[0],
            toType: 'image/png'
          }).then((convertHeicTopng: any) => {
            this.cleanObjectUrl();
            this.objectUrl = URL.createObjectURL(convertHeicTopng);
            this.imgUrl = this.objectUrl;
            this.forceDetectChanges();
          })
        } else {
          this.cleanObjectUrl();
          this.objectUrl = URL.createObjectURL(file);
          this.imgUrl = this.objectUrl;
          this.forceDetectChanges();
        }

        this.openModal();

      } else {
        this.errorEvent.emit('Not supported INPUT');
      }
    } else if (data instanceof File) {
      console.log("NgxPhotoEditor: File source detected", data);
      if (this.FILES_REGEX.test(data.name)) {
        if (!this.format) {
          this.format = data.type ? data.type.split('/')[1] : (data.name ? data.name.split('.').pop() : 'png');
        }
        this.cleanObjectUrl();
        this.objectUrl = URL.createObjectURL(data);
        this.imgUrl = this.objectUrl;
        this.forceDetectChanges();

        this.openModal();
      } else {
        this.errorEvent.emit('Not supported INPUT');
      }
    } else if (typeof data === 'string') {
      console.log("NgxPhotoEditor: String source detected (base64 or URL)");
      if ((/^data:image\/([a-zA-Z]*);base64,([^\"]*)$/).test(data)) {
        this.imgUrl = data;
        this.format = ((data.split(',')[0]).split(';')[0]).split(':')[1].split('/')[1];
        this.openModal();
      } else if ((/^data:application\/pdf;base64,([^\"]*)$/).test(data)) {
        this.pdfStringToBase64(data)
        this.openModal();
      } else if (this.isValidImageURL(data)) {
        this.imgUrl = data;
        this.format = data.split(/[#?]/)[0].split('.').pop()?.trim() ?? '';
        this.openModal();
      } else {
        this.errorEvent.emit('Not supported URL');
      }
    } else {
      this.errorEvent.emit('Not supported INPUT');
    }
  }

  constructor(
    private translate: TranslateService,
    public loadingController: LoadingController,
    public alertController: AlertController,
    private http: HttpClient,
    private changeDetectorRef: ChangeDetectorRef,
    private ngZone: NgZone,
    private platform: Platform) { }

  ngOnInit() {
  }

  forceDetectChanges() {
    setTimeout(() => {
      this.changeDetectorRef.detectChanges();
      this.ngZone.run(() => {
        this.changeDetectorRef.detectChanges();
      });
    }, 10);
  }

  cleanObjectUrl() {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }

  openModal() {
    if (this.isEmbed) {
      return;
    }
    setTimeout(() => {
      this.ngZone.run(() => {
        if (this.modal) {
          this.modal.present();
          this.forceDetectChanges();
        }
      });
    }, 10);
  }

  isValidImageURL(str: string) {
    return str.match(/^http[^\?]*.(jpg|jpeg|gif|png|tiff|bmp|pdf|jfif|heic|heif)(\?(.*))?(\#(.*))?$/gim);
  }

  rotateRight() {
    if (!this.cropper) return;
    const image = this.image.nativeElement;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = image.naturalHeight;
    canvas.height = image.naturalWidth;

    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(90 * Math.PI / 180);
    ctx.drawImage(image, -image.naturalWidth / 2, -image.naturalHeight / 2);

    this.imgUrl = canvas.toDataURL('image/' + this.format, this.quality);
    // The [src]="imgUrl" in the template will trigger (load)="onLoad()"
  }

  rotateLeft() {
    if (!this.cropper) return;
    const image = this.image.nativeElement;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = image.naturalHeight;
    canvas.height = image.naturalWidth;

    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(-90 * Math.PI / 180);
    ctx.drawImage(image, -image.naturalWidth / 2, -image.naturalHeight / 2);

    this.imgUrl = canvas.toDataURL('image/' + this.format, this.quality);
    // The [src]="imgUrl" in the template will trigger (load)="onLoad()"
  }

  crop() {
    if (!this.cropper) return;
    this.cropper.setDragMode('crop');
  }

  move() {
    if (!this.cropper) return;
    this.cropper.setDragMode('move');
  }

  zoomIn() {
    if (!this.cropper) return;
    this.cropper.zoom(0.1);
  }

  zoomOut() {
    if (!this.cropper) return;
    this.cropper.zoom(-0.1);
  }

  flipH() {
    if (!this.cropper) return;
    this.cropper.scaleY(-this.cropper.getImageData().scaleY);
    this.cropper.scaleY(-this.cropper.getImageData().scaleY);
    this.cropper.scaleX(-this.cropper.getImageData().scaleX);
    if (this.exportWhenUpdate) this.export(false);
  }

  flipV() {
    if (!this.cropper) return;
    this.cropper.scaleX(-this.cropper.getImageData().scaleX);
    this.cropper.scaleX(-this.cropper.getImageData().scaleX);
    this.cropper.scaleY(-this.cropper.getImageData().scaleY);
    if (this.exportWhenUpdate) this.export(false);
  }

  reset() {
    if (!this.cropper) return;
    this.cropper.reset();
  }

  export(isClose: boolean = true) {
    if (!this.cropper) return;
    this.isProcessing = true;
    this.imageLoaded = false;

    let width = this.resizeToWidth
    let height = this.resizeToHeight
    if (width == height) {
      let cropedImage = this.cropper.getCroppedCanvas();
      if (cropedImage.width > cropedImage.height) {
        width = null
      } else {
        height = null
      }
    }

    let cropedImage = this.cropper.getCroppedCanvas({
      width: width,
      height: height,
      imageSmoothingEnabled: this.imageSmoothingEnabled,
      imageSmoothingQuality: this.imageSmoothingQuality
    });

    const outputImage = cropedImage.toDataURL('image/' + this.format, this.quality);
    var blob = DataURIToBlob(outputImage);

    this.imageCroppedEvent.emit({
      base64: outputImage,
      file: new File([blob],
        Date.now() + '.' + this.format,
        { type: 'image/' + this.format })
    } as NgxCroppedEvent);

    this.isProcessing = false;
    this.imageLoaded = true;
    if (isClose) this.close();
  }


  close() {
    // ionModalWillDismiss will handle cleanup — avoid calling dismiss() twice
    this.modal.dismiss();
  }

  ionModalWillDismiss() {
    this.closeEvent.emit();
    this.isExist = false;
    this.imgUrl = null;
  }

  onModalDidPresent() {
    // Forcer la détection de changement après que le modal soit complètement présenté
    // Ceci est particulièrement important pour iOS
    if (this.platform.is('ios')) {
      setTimeout(() => {
        this.ngZone.run(() => {
          this.changeDetectorRef.detectChanges();
        });
      }, 50);
    }
  }

  onLoad() {
    if (this.image == null)
      return;

    this.closeBtnText = this.translate.instant('Close');
    this.applyBtnText = this.translate.instant('Apply');
    this.modalTitle = this.translate.instant('Photo Editor');

    if (this.cropper != null)
      this.cropper.destroy();

    this.cropper = new Cropper(this.image.nativeElement, {
      aspectRatio: this.aspectRatio,
      autoCropArea: this.autoCropArea,
      autoCrop: this.autoCrop,
      modal: this.mask, // black mask
      guides: this.guides, // grid
      center: this.centerIndicator, // center indicator
      viewMode: this.viewMode,
      scalable: this.scalable,
      zoomable: this.zoomable,
      background: false,
      cropBoxMovable: this.cropBoxMovable,
      cropBoxResizable: this.cropBoxResizable,
      ready: () => {
        this.imageReady();
      }
    });
  }

  imageReady() {
    this.imageLoaded = true;
    if (this.cropEnable == false)
      this.move()
    if (this.automaticalyClose) {
      this.export();
      this.imgUrl = null
    } else if (this.exportWhenUpdate) {
      this.export(false);
    }
  }

  ngOnDestroy(): void {
    this.cleanObjectUrl();
  }

  error() {
    this.errorEvent.emit('Error loading image');
  }

  async pdfToBase64(file: string) {
    var headers = new HttpHeaders({
      'content-type': 'application/json'
    });
    var formData = {
      'type': 'url',
      'url': file,
    }
    await this.presentLoading();
    this.http.post(environment.API_URL + 'documents/ws_public/getImageBase64FromPDF/', JSON.stringify(formData), { headers: headers }).subscribe(async (data: { [key: string]: any }) => {
      this.imgUrl = data['base64']
      this.loading?.dismiss()
      if (!data['base64']) {
        var alert = await this.alertController.create({
          header: this.translate.instant("Error"),
          subHeader: this.translate.instant("Invalid file"),
          message: this.translate.instant("The PDF file is invalid or too large."),
          buttons: ['OK'],
        });
        await alert.present();
      }
    }, async error => {
      this.loading?.dismiss()
      var alert = await this.alertController.create({
        header: this.translate.instant("Error"),
        subHeader: this.translate.instant("Invalid file"),
        message: this.translate.instant("The PDF file is invalid or too large."),
        buttons: ['OK'],
      });
      await alert.present();
    });
  }

  async pdfStringToBase64(data: string) {
    var headers = new HttpHeaders({
      'content-type': 'application/json'
    });
    var formData = {
      'type': 'base64',
      'base64': data,
    }
    await this.presentLoading();
    this.http.post(environment.API_URL + 'documents/ws_public/getImageBase64FromPDF/', JSON.stringify(formData), { headers: headers }).subscribe(async (data: { [key: string]: any }) => {
      this.source = data['base64']
      this.loading?.dismiss()
      if (!data['base64']) {
        var alert = await this.alertController.create({
          header: this.translate.instant("Error"),
          subHeader: this.translate.instant("Invalid file"),
          message: this.translate.instant("The PDF file is invalid or too large."),
          buttons: ['OK'],
        });
        await alert.present();
      }
    }, async error => {
      this.loading?.dismiss()
      var alert = await this.alertController.create({
        header: this.translate.instant("Error"),
        subHeader: this.translate.instant("Invalid file"),
        message: this.translate.instant("The PDF file is invalid or too large."),
        buttons: ['OK'],
      });
      await alert.present();
    });
  }

  async presentLoading() {
    this.loading = await this.loadingController.create({
      cssClass: 'my-custom-class',
      message: this.translate.instant('Please wait...'),
      duration: 600000
    });
    await this.loading?.present();
  }
}

export interface NgxCroppedEvent extends Event {
  base64?: string;
  file?: File;

}


export type imageFormat = 'gif' | 'jpeg' | 'tiff' | 'png' | 'webp' | 'bmp';
